/**
 * Sui Runtime Environment Detection
 *
 * Detects the runtime environment for Sui wallet interactions:
 * - Mobile: iOS or Android device (show wallet banner)
 * - Desktop: Desktop browser (show connect button only)
 *
 * Used to determine appropriate wallet connection UI and flows.
 */

import { getWallets } from "@mysten/wallet-standard";

export type SuiRuntimeEnv = {
  /** Any Sui wallet discovered via Wallet Standard (desktop/extension) */
  hasWalletStandard: boolean;

  /** Names of wallets discovered via Wallet Standard */
  discoveredWalletNames: string[];

  /** Device is mobile (iOS or Android) */
  isMobile: boolean;

  /** Detected platform: 'ios', 'android', or 'desktop' */
  platform: 'ios' | 'android' | 'desktop';

  /** Running in Slush in-app browser (iOS WebView) */
  isSlushWebView: boolean;
};

/**
 * Detect if running in Slush in-app browser (iOS WebView)
 *
 * @returns true if user is in Slush WebView, false otherwise
 */
export function isSlushWebView(): boolean {
  const ua = navigator.userAgent || '';

  const isIOS = /iPhone|iPad|iPod/.test(ua);
  if (!isIOS) return false;

  const isChromeIOS = /CriOS/i.test(ua);
  const isFirefoxIOS = /FxiOS/i.test(ua);
  const hasSafariToken = /Safari/i.test(ua);

  const looksLikeIOSWebView =
    !isChromeIOS &&
    !isFirefoxIOS &&
    !hasSafariToken;

  const isStandalone =
    (navigator as any).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches;

  const hasWindowWebkit = typeof (window as any).webkit !== 'undefined';

  return (
    isIOS &&
    looksLikeIOSWebView &&
    hasWindowWebkit &&
    !isStandalone
  );
}

/**
 * Detect the current Sui runtime environment
 *
 * @returns SuiRuntimeEnv object with environment details
 *
 * @example
 * ```typescript
 * const env = detectSuiEnv();
 *
 * if (env.isMobile) {
 *   // Mobile device - show wallet banner
 * } else {
 *   // Desktop - normal connect button only
 * }
 * ```
 */
export function detectSuiEnv(): SuiRuntimeEnv {
  // 1) Desktop/extensions: Wallet Standard discovery
  let discoveredWalletNames: string[] = [];
  let hasWalletStandard = false;

  try {
    const { get } = getWallets();
    const wallets = get();
    discoveredWalletNames = wallets.map((w: { name: string }) => w.name);
    hasWalletStandard = wallets.length > 0;
  } catch {
    // Library not present or running in early boot phase - no-op
  }

  // 2) User agent detection
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) ? navigator.userAgent : "";

  // 3) Platform detection
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;

  let platform: 'ios' | 'android' | 'desktop';
  if (isIOS) {
    platform = 'ios';
  } else if (isAndroid) {
    platform = 'android';
  } else {
    platform = 'desktop';
  }

  // 4) Slush WebView detection
  const isSlushBrowser = isSlushWebView();

  return {
    hasWalletStandard,
    discoveredWalletNames,
    isMobile,
    platform,
    isSlushWebView: isSlushBrowser,
  };
}

/**
 * Get platform-specific Slush wallet install URL
 *
 * @param platform - Platform to get URL for (defaults to auto-detected)
 * @returns App store or web URL for Slush wallet
 */
export function getSlushInstallUrl(platform?: 'ios' | 'android' | 'desktop'): string {
  const detectedPlatform = platform || detectSuiEnv().platform;

  switch (detectedPlatform) {
    case 'ios':
      return 'https://apps.apple.com/app/id6476572140';
    case 'android':
      return 'https://play.google.com/store/apps/details?id=com.mystenlabs.suiwallet';
    case 'desktop':
    default:
      return 'https://slush.app';
  }
}

